import {
    Component,
    ElementRef,
    Input,
    AfterViewInit,
    ViewChild,
    OnChanges,
    SimpleChanges,
    OnInit,
    OnDestroy
} from '@angular/core';
import { ModalService } from 'shared/services';
import { ModalOptions, Position, Size } from 'app/shared/components/modal/types';

interface HammerTapEvent extends HammerInput {
    tapCount: number;
}

@Component({
    selector: 'fc-image-dialog',
    templateUrl: './image-dialog.component.html',
    styleUrls: ['./image-dialog.component.less'],
    standalone: false
})
export class ImageDialogComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
    @Input() isMobile = false;
    @Input() image: string;

    @Input() modalId: string;
    @Input() options: ModalOptions = {
        size: Size.FullScreen,
        position: Position.Center
    };

    @ViewChild('container') containerRef: ElementRef<HTMLDivElement>;
    @ViewChild('imageElement') imageRef: ElementRef<HTMLDivElement>;
    @ViewChild('responsive') responsiveRef: ElementRef<HTMLImageElement>;

    public zoomEnabled = false;

    private initialSize: [horizontalSize: number, verticalSize: number];
    private currentPosition: [horizontalPosition: number, verticalPosition: number];
    private windowSize: { width: number; height: number };

    constructor(private modalService: ModalService) {}

    ngOnInit(): void {
        window.addEventListener('resize', this.setInitialSize);
    }

    ngOnDestroy(): void {
        window.removeEventListener('resize', this.setInitialSize);
    }

    ngAfterViewInit(): void {
        this.imageRef.nativeElement.style.backgroundImage = `url(${this.image})`;
        this.responsiveRef.nativeElement.onload = this.setInitialSize;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.imageRef?.nativeElement && this.responsiveRef?.nativeElement) {
            this.imageRef.nativeElement.style.backgroundImage = `url(${this.image})`;
            this.responsiveRef.nativeElement.onload = this.setInitialSize;
        }
    }

    panHandle(event: HammerInput) {
        if (!this.zoomEnabled) {
            return;
        }

        const { deltaX, deltaY } = event;

        let horizontalPosition = this.currentPosition[0] - this.getPercentage(deltaX * 2, this.windowSize.width);
        let verticalPosition = this.currentPosition[1] - this.getPercentage(deltaY * 2, this.windowSize.height);

        if (horizontalPosition > 100) {
            horizontalPosition = 100;
        }

        if (horizontalPosition < 0) {
            horizontalPosition = 0;
        }

        if (verticalPosition > 100) {
            verticalPosition = 100;
        }

        if (verticalPosition < 0) {
            verticalPosition = 0;
        }

        this.imageRef.nativeElement.style.backgroundPosition = `${horizontalPosition}% ${verticalPosition}%`;

        if (event.isFinal) {
            this.currentPosition = [horizontalPosition, verticalPosition];
        }
    }

    tapHandle(event: HammerTapEvent) {
        if (this.isMobile) {
            if (event.tapCount % 2 === 0) {
                this.toggleZoom(event.srcEvent as MouseEvent);
            }
        } else {
            this.toggleZoom(event.srcEvent as MouseEvent);
        }
    }

    toggleZoom(event: MouseEvent) {
        if (this.zoomEnabled) {
            this.zoomEnabled = false;
            this.imageRef.nativeElement.style.backgroundSize = `${this.initialSize[0]}% ${this.initialSize[1]}%`;
            this.imageRef.nativeElement.style.backgroundPosition = '50% 50%';
        } else {
            this.zoomEnabled = true;
            const sizeX = this.initialSize[0] * 2;
            const sizeY = this.initialSize[1] * 2;

            this.imageRef.nativeElement.style.backgroundSize = `${sizeX}% ${sizeY}%`;
            this.setPosition(event);
        }
    }

    getPercentage(value: number, total: number) {
        return Math.floor((value / total) * 100);
    }

    setPosition(event: MouseEvent) {
        const { offsetX, offsetY } = event;
        const positionX = this.getPercentage(offsetX, this.windowSize.width);
        const positionY = this.getPercentage(offsetY, this.windowSize.height);
        this.currentPosition = [positionX, positionY];

        this.imageRef.nativeElement.style.backgroundPosition = `${positionX}% ${positionY}%`;
    }

    setInitialSize = () => {
        let { naturalHeight: height, naturalWidth: width } = this.responsiveRef.nativeElement;
        this.windowSize = { width: window.innerWidth, height: window.innerHeight };

        let horizontalScaleFactor = 1;
        let verticalScaleFactor = 1;

        if (height > this.windowSize.height) {
            verticalScaleFactor = height / this.windowSize.height;
        }

        if (width > this.windowSize.width) {
            horizontalScaleFactor = width / this.windowSize.width;
        }

        const scaleFactor = horizontalScaleFactor > verticalScaleFactor ? horizontalScaleFactor : verticalScaleFactor;

        height = height / scaleFactor;
        width = width / scaleFactor;

        this.initialSize = [
            this.getPercentage(width, this.windowSize.width),
            this.getPercentage(height, this.windowSize.height)
        ];

        this.imageRef.nativeElement.style.backgroundSize = `${this.initialSize[0]}% ${this.initialSize[1]}%`;
        this.zoomEnabled = false;
    };

    handleClose() {
        this.modalService.close(this.modalId);
    }
}
